<script setup>
// The tool editor (phase 4.7, D-G6 + Q1, R2-4-6 = A): the kit's EditorDrawer instead of the dialog with its own
// scroll. The form is the same CustomToolForm, in sections that open and close; Save sits in the drawer's header and
// is on only with a change; closing with changes asks first ("Discard changes?"). Same store calls and messages as
// the dialog it replaces (create or update, then close).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import EditorDrawer from 'dashboard/components-next/polaris/cards/EditorDrawer.vue';
import CustomToolForm from './CustomToolForm.vue';

const props = defineProps({
  // null: a new tool
  tool: { type: Object, default: null },
});
const open = defineModel('open', { type: Boolean, default: false });

const { t } = useI18n();
const store = useStore();
const form = ref(null);
const drawer = ref(null);

const isEdit = computed(() => !!props.tool?.id);
const title = computed(() =>
  isEdit.value ? props.tool.title : t('POLARIS.PAGES.TOOL_EDITOR.NEW_TOOL')
);

const onSubmit = async toolDetails => {
  try {
    if (isEdit.value) {
      await store.dispatch('polarisCustomTools/update', {
        id: props.tool.id,
        ...toolDetails,
      });
      useAlert(t('POLARIS.CUSTOM_TOOLS.EDIT.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('polarisCustomTools/create', toolDetails);
      useAlert(t('POLARIS.CUSTOM_TOOLS.CREATE.SUCCESS_MESSAGE'));
    }
    form.value?.markSaved();
    open.value = false;
  } catch (error) {
    const fallback = isEdit.value
      ? t('POLARIS.CUSTOM_TOOLS.EDIT.ERROR_MESSAGE')
      : t('POLARIS.CUSTOM_TOOLS.CREATE.ERROR_MESSAGE');
    useAlert(parseAPIErrorResponse(error) || fallback);
  }
};

// The page switches rows through the drawer's discard guard (asks first when there are unsaved changes).
defineExpose({ runGuarded: action => drawer.value?.runGuarded(action) });
</script>

<template>
  <EditorDrawer
    ref="drawer"
    v-model:open="open"
    :title="title"
    :title-editable="false"
    :kind="t('POLARIS.PAGES.TOOL_EDITOR.KIND')"
    :dirty="form?.dirty ?? false"
    :can-save="!form?.isLoading"
    :save-label="isEdit ? t('POLARIS.FORM.EDIT') : t('POLARIS.FORM.CREATE')"
    data-tool-editor
    @save="form?.submit()"
  >
    <p class="type-polaris-body text-polaris-muted-foreground">
      {{ t('POLARIS.CUSTOM_TOOLS.FORM_DESCRIPTION') }}
    </p>
    <CustomToolForm
      :key="tool?.id ?? 'new'"
      ref="form"
      :mode="isEdit ? 'edit' : 'create'"
      :tool="tool ?? {}"
      @submit="onSubmit"
    />
  </EditorDrawer>
</template>
